import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import {
  BillReminderRequest,
  BillReminderResponse,
  BillReminderSearchResponse,
  MandateBankDto,
  MandateBillDto,
  PatientDto,
  ReminderDto
} from 'ts-types/api.types';
import { CancelTokenSource } from 'axios';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { RouteComponentProps } from 'react-router';
import axios from 'services/http';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import styled from 'styled-components';
import { Button, Grid, Header } from 'semantic-ui-react';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import ErrorMessage from 'components/final-form/ErrorMessage';
import DatePicker from 'components/final-form/DatePicker';
import Select from 'components/final-form/Select';
import LoaderComponent from 'components/LoaderComponent';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { getAllMandateBanksByMandateId } from 'services/mandateBankServices';
import { FormApi } from 'final-form';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import { BillStatus, BillType } from 'ts-types/api.enums';
import ToolbarButton from 'components/layout/ToolbarButton';
import { getAllMandateBillsByMandateId } from 'services/mandateBillServices';
import moment from 'moment';
import { getBillReminderPdfUrl, remindBills } from 'services/billReminderServices';
import createDecorator from 'final-form-calculate';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PxGrid from 'components/layout/PxGrid';
import PdfModal from 'components/pdf-modal/PdfModal';
import { numberFormatter } from 'utils/fomattingUtils';

const StyledFinalFormContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding: 1em 1em;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:nth-child(4) {
    text-align: right;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }
`;

const ReminderRow = styled(Grid.Row)`
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
  height: 100%;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }

  .header-data {
    margin-top: 0.2rem;
    margin-bottom: 1rem;
    font-weight: 300;
  }
`;

const TableContainer = styled.div`
  height: 100%;
  min-height: 300px;
`;

const StyledHeader = styled(Header)`
  margin-top: 0 !important;
`;

const ReminderDate = styled(Grid.Row)`
  padding-bottom: 0.25rem !important;
`;

const MandateBank = styled(Grid.Row)`
  padding-top: .25rem !important;
`;

const TotalRemindedColumn = styled(Grid.Column)`
  margin-top: .5rem;
  padding-top: .5rem;
`;

const StyledBillResults = styled.span`
  float: right;
  text-align: right;
  font-weight: 300;
  font-size: 1rem;

  &.margin-top {
    margin-top: 0.5em;
  }

  .results-data {
    color: rgb(48 145 218);
    font-weight: bold;
    margin-left: 0.5rem;
  }
`;

const errorMessageStyle = {
  marginBottom: "1rem"
};

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const numberColumnStyle = {
  floating: "right",
  paddingRight: "0.5rem"
};

interface FinalFormData {
  bills: Array<BillReminderSearchResponse>,
  selected?: BillReminderSearchResponse,
  reminderDate: string,
  mandateBankId: number,
  proofList: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  bills: Array<BillReminderSearchResponse>,
  billsLoading: boolean,
  currentPatient?: PatientDto,
  onPatientRefresh: (patient?: PatientDto) => void,
}

interface State {
  cancelTokenSource: CancelTokenSource,
  formData: Partial<FinalFormData>,
  mandateBanks: Array<DropdownOption>,
  mandateBills: Array<MandateBillDto>,
  billReminderPdfModalOpen: boolean,
  savedBillReminders: Array<BillReminderResponse>
}

class ManualBillReminderTableView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {options: {workDate}, currentUser} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    const formData: Partial<FinalFormData> = {
      reminderDate: workDate,
      proofList: false
    };

    this.state = {
      cancelTokenSource: cancelTokenSource,
      formData: formData,
      mandateBanks: [],
      mandateBills: [],
      billReminderPdfModalOpen: false,
      savedBillReminders: []
    };

    if (currentUser) {

      const {mandateId} = currentUser.userConfig;

      const methods: Array<Promise<any>> = [
        getAllMandateBanksByMandateId(mandateId, cancelTokenSource),
        getAllMandateBillsByMandateId(mandateId, cancelTokenSource)
      ];

      Promise.all(methods)
          .then(responses => {

            const mappedBanks: Array<DropdownOption> = responses[0].map((mandateBank: MandateBankDto) => ({
              key: mandateBank.id,
              text: `${mandateBank.description}, ${mandateBank.city}, (${mandateBank.bankAccount})`,
              value: mandateBank.id
            }));

            this.setState({
              mandateBanks: mappedBanks,
              mandateBills: responses[1],
              formData: {
                ...formData,
                mandateBankId: mappedBanks[0].value
              }
            })
          });
    }
  }

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData} = this.state;
    const {bills, billsLoading} = this.props;

    if (prevBillsLoading != billsLoading && !billsLoading) {

      const deepCopiedBills: Array<BillReminderSearchResponse> = JSON.parse(JSON.stringify(bills));

      this.setState({
        formData: {
          reminderDate: formData.reminderDate,
          mandateBankId: formData.mandateBankId,
          bills: deepCopiedBills,
          selected: undefined
        }
      })
    }
  }

  reminderDecorator = createDecorator({
    field: "bills",
    updates: (...params) => {

      const {bills} = params[2] as FinalFormData;
      const {bills: prevBills} = params[3] as FinalFormData;

      if (bills && prevBills) {
        const foundModifiedIndex = bills
            .findIndex((bill, index) => prevBills[index] && bill.billReminders != prevBills[index].billReminders);

        if (foundModifiedIndex != -1) {
          const modifiedBills = Array.from(bills);
          if (bills[foundModifiedIndex].billReminders > prevBills[foundModifiedIndex].billReminders) {
            modifiedBills[foundModifiedIndex].billId += "*";
          } else {
            modifiedBills[foundModifiedIndex].billId = modifiedBills[foundModifiedIndex].billId.slice(0, -1);
          }

          return {
            bills: modifiedBills
          }
        }
      }

      return {};
    }
  });

  openBillReminderPdfModal = (billReminderResponses: Array<BillReminderResponse>) => {
    this.setState({
      billReminderPdfModalOpen: true,
      savedBillReminders: billReminderResponses
    })
  };

  closeBillReminderPdfModal = (values: Partial<FinalFormData>) => {
    const {history} = this.props;

    this.setState({
      billReminderPdfModalOpen: false,
      savedBillReminders: []
    });

    if (!values.proofList) {
      history.push("/");
    }
  };

  onAddReminder = (form: FormApi, rowData: BillReminderSearchResponse, rowIndex: number) => {

    const modifiedData = {
      ...rowData
    };

    modifiedData.billReminders++;

    form.change(`bills[${rowIndex}]`, modifiedData);
  };

  onRemoveReminder = (form: FormApi, rowData: BillReminderSearchResponse, rowIndex: number) => {

    const {billReminders} = rowData;

    const modifiedData: BillReminderSearchResponse = {
      ...rowData,
      billReminders: billReminders - 1
    };

    form.change(`bills[${rowIndex}]`, modifiedData);
  };

  getBillsToBeReminded = (currentBills: Array<BillReminderSearchResponse>): Array<BillReminderSearchResponse> => {

    const {bills} = this.props;

    if (bills.length == currentBills.length) {
      return currentBills.filter((bill, index) => {
        return bill.billReminders != bills[index].billReminders;
      });
    }

    return [];
  };

  constructModifiedReminders = (prevBillReminders: number, billReminders: number, reminderDate: string): Array<JSX.Element> => {

    const {getMessage} = this.props;
    const {mandateBills} = this.state;

    const date = moment(reminderDate).format("DD.MM.YYYY");

    const modifiedReminders: Array<JSX.Element> = [];

    if (prevBillReminders > billReminders) {
      for (let i = 1; i <= prevBillReminders - billReminders; i++) {
        modifiedReminders.push((
            <ReminderRow key={i}>
              <Grid.Column>
                <Header as="h4">
                  {`${i + billReminders}. ${getMessage("bill.reminderDeleted")}`}
                </Header>
              </Grid.Column>
            </ReminderRow>
        ))
      }
    } else if (prevBillReminders < billReminders) {
      for (let i = prevBillReminders; i < billReminders; i++) {

        const mandateBill = mandateBills[i + 1];
        let amount = 0;
        let dueDate = date;

        if (mandateBill) {
          dueDate = moment(reminderDate).add(mandateBill.period, "days").format("DD.MM.YYYY");
          amount = mandateBill.surcharge;
        }

        modifiedReminders.push((
            <ReminderRow key={i}>
              <Grid.Column width={11} verticalAlign="middle">
                <Header as="h4">
                  {`${i + 1}. ${getMessage("bill.reminder")}, ${date}`}
                </Header>
              </Grid.Column>
              <Grid.Column width={5} verticalAlign="middle">
                <Header as="h4">
                  {numberFormatter.format(amount)}&emsp;{dueDate}
                </Header>
              </Grid.Column>
            </ReminderRow>
        ))
      }
    }

    return modifiedReminders;
  };

  billStatusCellRenderer = (data: any) => {

    const {bills} = this.props;

    const rowData: BillReminderSearchResponse = data.rowData;
    const rowIndex = data.rowIndex;

    const billIconRequest: BillIconRequest = {
      hasReminders: bills[rowIndex] ? bills[rowIndex].billReminders > 0 : false,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };

    return <div>{getBillIcon(billIconRequest)}</div>
  };

  billDateCellRenderer = (field: string) => (data: any) =>
      <div>{moment(data.rowData[field]).format("DD.MM.YYYY")}</div>;

  amountCellRenderer = (data: any) =>
      <div style={numberColumnStyle}>{(data.rowData.amount)}</div>;

  billActionsCellRenderer = (form: FormApi) => (data: any) => {

    const {getMessage} = this.props;

    const rowData: BillReminderSearchResponse = data.rowData;
    const rowIndex = data.rowIndex;

    return (
        <div>
          <ToolbarButton iconName='add'
                         iconSize='small'
                         onClick={() => this.onAddReminder(form, rowData, rowIndex)}
                         dataTooltip={getMessage('bill.addReminder')}
                         disabled={rowData.billReminders == 3}
          />
          <ToolbarButton iconName='minus'
                         iconSize='small'
                         onClick={() => this.onRemoveReminder(form, rowData, rowIndex)}
                         dataTooltip={getMessage('bill.removeReminder')}
                         disabled={rowData.billReminders == 0}
          />
        </div>
    )
  };

  billsRowGetter = (bills: Array<BillReminderSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (form: FormApi) => ({className, columns, index, key, style}: any) => {
    const a11yProps = {"aria-rowindex": index + 1};

    const {bills = [], selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style};

    if (selected && bills[index].id == selected.id) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
        >
          {columns}
        </div>
    );
  };

  setAutomaticReminderProoflist = (form: FormApi, proofList: boolean) => {
    form.change("proofList", proofList);
  }

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {cancelTokenSource} = this.state;
    const {currentUser, bills: prevBills, history, getMessage, currentPatient} = this.props;

    if (currentUser) {
      const {mandateId} = currentUser.userConfig;
      const {reminderDate, bills = [], mandateBankId} = values;

      const remindersToBeAdded: Array<ReminderDto> = [];
      const remindersToBeDeleted: Array<ReminderDto> = [];

      bills.forEach((bill: BillReminderSearchResponse, index: number) => {
        if (bill.billReminders != prevBills[index].billReminders) {
          if (bill.billReminders > prevBills[index].billReminders) {
            remindersToBeAdded.push({
              billId: bill.id,
              numberOfReminders: bill.billReminders - prevBills[index].billReminders
            })
          } else {
            remindersToBeDeleted.push({
              billId: bill.id,
              numberOfReminders: prevBills[index].billReminders - bill.billReminders
            });
          }
        }
      });

      if (remindersToBeAdded.length != 0 || remindersToBeDeleted.length != 0) {
        const billReminderRequest: Partial<BillReminderRequest> = {
          reminderDate: reminderDate,
          mandateBankId: mandateBankId,
          mandateId: mandateId,
          remindersToAdd: remindersToBeAdded,
          remindersToDelete: remindersToBeDeleted,
          proofList: values.proofList
        };

        let error = undefined;
        const refreshPatient = () => this.props.onPatientRefresh(currentPatient);

        await remindBills(billReminderRequest, cancelTokenSource)
            .then(response => {
              if (response.length > 0) {
                this.openBillReminderPdfModal(response);
              } else {
                history.push("/");
              }
              if (currentPatient) {
                refreshPatient();
              }
            })
            .catch(() => {
              error = getMessage("error.general");
            });

        if (error) {
          return {
            error: error
          }
        }
      } else {
        return {
          error: getMessage("error.noBillsToRemind")
        }
      }
    }
  };

  render(): React.ReactNode {
    const {formData} = this.state;
    return (
        <StyledFinalFormContainer>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              decorators={[this.reminderDecorator]}
              mutators={{
                ...arrayMutators
              }}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
        </StyledFinalFormContainer>
    );
  }

  renderForm = ({submitting, values, handleSubmit, form}: FormRenderProps): JSX.Element => {

    const {bills = [], selected, reminderDate} = values;
    const {mandateBanks, billReminderPdfModalOpen, savedBillReminders} = this.state;
    const {bills: prevBills, billsLoading, getMessageWithNamedParams, getMessage} = this.props;

    const billsLength = bills.length;

    const billsToBeReminded = this.getBillsToBeReminded(bills);
    let modifiedReminders: Array<JSX.Element> = [];

    if (selected) {
      const foundPrevBill = prevBills.find(bill => bill.id == selected.id);
      const foundBill = bills.find((bill: BillReminderSearchResponse) => bill.id == selected.id);

      if (foundPrevBill) {

        const {billReminders: prevBillReminders} = foundPrevBill;
        const {billReminders} = foundBill;

        modifiedReminders = this.constructModifiedReminders(prevBillReminders, billReminders, reminderDate);
      }
    }

    const disableBtn = billsToBeReminded.length < 1;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent>
            <StyledFormContent>
              <StyledContainer style={{width: "60%"}}>
                <Field
                    name="error"
                    component={ErrorMessage}
                    style={errorMessageStyle}
                />

                <StyledHeader>
                  {getMessage("bills")}
                  <StyledBillResults>
                    {getMessage("bills.manualReminderBilLResults")}
                    <span className={"results-data"}>{billsLength}</span>
                  </StyledBillResults>
                </StyledHeader>

                <TableContainer>
                  {billsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                      : this.renderTable(bills, form)}
                </TableContainer>
                <TotalRemindedColumn width={16}>
                  <Header as="h3" textAlign="right">
                    {
                      getMessageWithNamedParams("bills.numberOfBillsToBeReminded", {
                        billsToBeReminded: billsToBeReminded.length
                      })
                    }
                  </Header>
                </TotalRemindedColumn>
              </StyledContainer>

              <StyledContainer style={{width: "40%"}}>
                <PxGrid>
                  <ReminderDate>
                    <Grid.Column width={4} verticalAlign="middle">{getMessage("reminderDate")}:</Grid.Column>
                    <Grid.Column width={5}>
                      <Field
                          name="reminderDate"
                          component={DatePicker}
                          fluid
                      />
                    </Grid.Column>
                  </ReminderDate>
                  <MandateBank>
                    <Grid.Column width={4} verticalAlign="middle">{getMessage("bill.bank")}:</Grid.Column>
                    <Grid.Column width={10}>
                      <Field
                          name="mandateBankId"
                          component={Select}
                          options={mandateBanks}
                          fluid
                      />
                    </Grid.Column>
                  </MandateBank>
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("patient")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected &&
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                        <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("guarantor")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected && selected.guarantorDescription &&
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h4" className={"header-data"}>
                        <div>{`${selected.guarantorDescription}`}</div>
                        <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                </PxGrid>
                <PxGrid>
                  <Grid.Row>
                    <Grid.Column verticalAlign="middle">
                      <Header>
                        {getMessage("bill.reminderInformation")}
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  {selected &&
                  <Grid.Row>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Header as="h4">
                        <div>{getMessage("bill.paymentsReceived")}</div>
                      </Header>
                    </Grid.Column>
                    <Grid.Column width={5} verticalAlign="middle">
                      <Header as="h4">
                        <div>{
                          `${selected.paidAmount
                              ? numberFormatter.format(selected.paidAmount)
                              : numberFormatter.format(0)}`
                        }
                        </div>
                      </Header>
                    </Grid.Column>
                  </Grid.Row>
                  }
                  {modifiedReminders}
                </PxGrid>
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <FormButtonsRow>
            <Button type="submit"
                    compact
                    primary
                    disabled={submitting || disableBtn}
                    onClick={() => this.setAutomaticReminderProoflist(form, false)}
            >
              {getMessage("remind")}
            </Button>
            <Button type="submit"
                    compact
                    secondary
                    disabled={submitting || disableBtn}
                    onClick={() => this.setAutomaticReminderProoflist(form, true)}
            >
              {getMessage("bill.reminderProoflist")}
            </Button>
          </FormButtonsRow>
          {
            billReminderPdfModalOpen &&
            <PdfModal
                modalOpen={billReminderPdfModalOpen}
                onCloseModal={() => this.closeBillReminderPdfModal(values)}
                pdfResponses={savedBillReminders}
                getPdfUrl={this.getBillReminderPdfUrl}
            />
          }
        </StyledForm>
    )
  };

  getBillReminderPdfUrl = (billReminderResponse: BillReminderResponse) => {
    return getBillReminderPdfUrl(billReminderResponse.pdfId, billReminderResponse.pdfName);
  };

  renderTable = (bills: Array<BillReminderSearchResponse>, form: FormApi): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form)}
            rowCount={bills.length}
            columns={[
              {
                width: 70,
                label: getMessage("bill"),
                dataKey: "status",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                label: "#",
                dataKey: "billId"
              },
              {
                width: 100,
                label: getMessage("bill.date"),
                dataKey: "date",
                cellRenderer: this.billDateCellRenderer("date")
              },
              {
                width: 150,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.amountCellRenderer
              },
              {
                width: 100,
                label: getMessage("dueDate"),
                dataKey: "dueDate",
                cellRenderer: this.billDateCellRenderer("dueDate")
              },
              {
                width: 100,
                label: getMessage("actions"),
                dataKey: "actions",
                cellRenderer: this.billActionsCellRenderer(form)
              }
            ]}
        />
    )
  };
}

const ManualBillReminderTableViewWrapper =
    withMessages(
        withAuthContext(
            withRouterWorkaround(
                ManualBillReminderTableView)));

export default ManualBillReminderTableViewWrapper;
